import { FunctionalComponent, h } from '@stencil/core'
import { JSXBase } from '@stencil/core/internal'
import Axios from 'axios'
import { ICompany } from 'src/call/types'
import { openUrl } from 'src/zoom-app/utils'

import { AppSharedPropsStore } from '../app'
import { CrmProvider } from '../crm/types'
import { iconSizeForCrmProvider, textColorForCrmProvider } from './crm'
import { colorClasses } from './css'

export function getAllURLSearchParams(): Record<string, string> {
  const urlParams = new URLSearchParams(window.location.search)
  const params = {}
  urlParams.forEach((v, k, _p) => (params[k] = v))
  return params
}

export function escape(url: string): string {
  return encodeURI(url)
}

interface IUserTokenResponse {
  user_token: string
}

export async function openUrlInNewWindow(url: string) {
  let newUrl = url
  if (AppSharedPropsStore.state.userTokenUrl) {
    await Axios.get<IUserTokenResponse>(AppSharedPropsStore.state.userTokenUrl, { timeout: 1000 })
      .then((result) => (newUrl = addParametersToUrl(url, result.data)))
      .catch(() => {}) // Do nothing on errors
  }

  if (AppSharedPropsStore.state.inZapp) {
    await openUrl(newUrl)
  } else {
    // eslint-disable-next-line
    window.open(newUrl)
  }
}

export const ExternalLink: FunctionalComponent<JSXBase.IntrinsicElements['a']> = (
  { class: className, ...props },
  children
) => (
  <a
    {...props}
    class={`${className} cursor-pointer`}
    title={props.href}
    onClick={async (e) => {
      e.preventDefault()
      e.stopImmediatePropagation()
      await openUrlInNewWindow(props.href)
    }}
  >
    {children}
  </a>
)

export const ExternalLinkIcon: FunctionalComponent<{ provider: CrmProvider | 'linkedin' }> = ({ provider }) => {
  const textColor = {
    closeio: textColorForCrmProvider('closeio'),
    hubspot: textColorForCrmProvider('hubspot'),
    salesforce: textColorForCrmProvider('salesforce'),
    linkedin: '#2867B2',
  }[provider]
  const iconSize = {
    closeio: iconSizeForCrmProvider('closeio'),
    hubspot: iconSizeForCrmProvider('hubspot'),
    salesforce: iconSizeForCrmProvider('salesforce'),
    linkedin: 'xl',
  }[provider]
  return <ui-icon icon={provider} class={`${colorClasses({}, { hover: { text: textColor } })} text-${iconSize}`} />
}

export function addTimezoneParameterTo(url: string) {
  // https://caniuse.com/mdn-javascript_builtins_intl_datetimeformat_resolvedoptions_computed_timezone
  return addParametersToUrl(url, { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone })
}

export function addParametersToUrl(url: string, parameters: Record<string, any>) {
  let newUrl: URL
  try {
    newUrl = new URL(url)
  } catch {
    // If the URL is not parsable as-is, it might just be a path.  Append the origin as the base.
    newUrl = new URL(url, window.location.origin)
  }

  // eslint-disable-next-line
  for (let key in parameters) {
    if (newUrl.searchParams.has(key)) {
      newUrl.searchParams.delete(key)
    }
    newUrl.searchParams.append(key, parameters[key])
  }

  return newUrl.toString()
}

export function getUrl(domain: string) {
  if (domain.startsWith('http')) {
    return domain
  }
  return 'https://' + domain.replace(/^.*\/\//, '')
}

/**
 * Adds `https://` to a company's `domain`
 */
export function getCompanyUrl(company: ICompany) {
  return company?.domain && getUrl(company.domain)
}

export function removeQueryParamFromUrl(paramKey: string) {
  const queryParams = new URLSearchParams(window.location.search)
  if (queryParams.get(paramKey)) {
    queryParams.delete(paramKey)
    const queryString = queryParams.toString()
    history.replaceState(null, null, queryString ? `?${queryString}` : location.pathname)
  }
}
